import { useEffect, useRef } from "react"
import { autoSaveTimeout } from "../../../service/multilineEditorService"

interface UseAutoSaveProps {
    enabled: boolean
    lastSave: number
    setLastSave: (time: number) => void
    onConfirm: () => void
}

export const useAutoSave = ({ enabled, lastSave, setLastSave, onConfirm }: UseAutoSaveProps) => {
    const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Store the latest values in refs to avoid stale closures
    const lastSaveRef = useRef(lastSave)
    const setLastSaveRef = useRef(setLastSave)
    const onConfirmRef = useRef(onConfirm)
    const enabledRef = useRef(enabled)

    // Update refs when values change
    useEffect(() => {
        lastSaveRef.current = lastSave
    }, [lastSave])

    useEffect(() => {
        setLastSaveRef.current = setLastSave
        onConfirmRef.current = onConfirm
    }, [setLastSave, onConfirm])

    useEffect(() => {
        enabledRef.current = enabled
    }, [enabled])

    useEffect(() => {
        const checkAndSave = () => {
            const curTime = new Date().getTime()
            if (curTime > lastSaveRef.current + autoSaveTimeout) {
                setLastSaveRef.current(curTime)
                if (lastSaveRef.current > 0) {
                    onConfirmRef.current()
                }
            }
            // Schedule next check if still enabled
            if (enabledRef.current) {
                timeoutIdRef.current = setTimeout(checkAndSave, 1000)
            }
        }

        if (!enabled) {
            // Clear timeout if autoSave gets disabled
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current)
                timeoutIdRef.current = null
            }
            return
        }

        // Start the periodic check
        timeoutIdRef.current = setTimeout(checkAndSave, 1000)

        return () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current)
                timeoutIdRef.current = null
            }
        }
    }, []) // Empty dependency array since we're using refs for all values
}
